import React, { useState, useEffect } from "react"
import { Button, Flex, Dropdown, Card, Spin } from "antd"
import { MoreOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { getCrewTimeOff, createCrewTimeOff, updateCrewTimeOff, deleteCrewTimeOff } from "store/crew-profiles"
import { toaster } from "components/common/Toast"
import DataTable from "ui/DataTable"
import CustomPagination from "components/common/CustomPagination"
import { ConfirmModal } from "ui/ConfirmModal"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import BookingEmptyImage from "ui/Icon/DSIcon/BookingEmptyImage"
import { Text } from "ui/Typography"
import { colors } from "themes/variables"
import ScheduleTimeOffModal from "./modal/ScheduleTimeOff"
import BookingConflictModal from "components/common/BookingConflictModal"
import { useBookingConflict } from "hooks/useBookingConflict"
import styles from "./index.module.css"
import PropTypes from "prop-types"

const ScheduledTimeOff = ({ crewMemberId, can_edit }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 4
  const [sortKey, setSortKey] = useState(null)
  const [sortValue, setSortValue] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [recordToDelete, setRecordToDelete] = useState(null)
  const [currentAction, setCurrentAction] = useState(null) // Track which action is being performed: 'save' or 'saveAndAddNew'

  // Use booking conflict hook
  const { handleBookingConflictError, bookingConflictModalProps } = useBookingConflict()

  // Get time off data from Redux store
  const { data: timeOffData = [], loading, metadata } = useSelector((state) => state.crewProfiles.timeOff)
  const { loading: creating } = useSelector((state) => state.crewProfiles.createTimeOff)
  const { loading: updating } = useSelector((state) => state.crewProfiles.updateTimeOff)
  const { loading: deleting } = useSelector((state) => state.crewProfiles.deleteTimeOff)

  // Fetch time off data with server-side pagination and sorting
  const fetchTimeOffData = () => {
    if (crewMemberId) {
      const params = {
        time_frame: "upcoming",
        page: currentPage,
        per_page: pageSize
      }

      if (sortKey && sortValue) {
        params.sort_key = sortKey
        params.sort_value = sortValue
      }

      dispatch(getCrewTimeOff(crewMemberId, params))
    }
  }

  // Fetch time off data on mount and when dependencies change
  useEffect(() => {
    fetchTimeOffData()
  }, [dispatch, crewMemberId, currentPage, pageSize, sortKey, sortValue])

  const handleAdd = () => {
    setEditingRecord(null)
    setIsModalOpen(true)
  }

  const handleEdit = (record) => {
    setEditingRecord(record)
    setIsModalOpen(true)
  }

  const handleDelete = (record) => {
    setRecordToDelete(record)
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = () => {
    if (recordToDelete && crewMemberId) {
      dispatch(deleteCrewTimeOff(crewMemberId, recordToDelete.id))
        .then(() => {
          toaster.success(t("crewMember.scheduledTimeOff.messages.deleteSuccess"))
          setDeleteConfirmOpen(false)
          setRecordToDelete(null)

          // If we're deleting the last item on the current page and not on page 1, go to page 1
          if (timeOffData.length === 1 && currentPage > 1) {
            setCurrentPage(1)
          } else {
            fetchTimeOffData()
          }
        })
        .catch((error) => {
          console.error("Error deleting time off:", error)
          setDeleteConfirmOpen(false)
          setRecordToDelete(null)
        })
    } else {
      setDeleteConfirmOpen(false)
      setRecordToDelete(null)
    }
  }

  const cancelDelete = () => {
    setDeleteConfirmOpen(false)
    setRecordToDelete(null)
  }


  const handleSave = (values) => {
    setCurrentAction('save')
    const action = editingRecord
      ? updateCrewTimeOff(crewMemberId, editingRecord.id, values)
      : createCrewTimeOff(crewMemberId, values)

    return dispatch(action)
      .then(() => {
        const message = editingRecord
          ? t("crewMember.scheduledTimeOff.messages.updateSuccess")
          : t("crewMember.scheduledTimeOff.messages.createSuccess")
        toaster.success(message)
        setIsModalOpen(false)
        setEditingRecord(null)
        setCurrentAction(null)
        fetchTimeOffData()
      })
      .catch((error) => {
        setCurrentAction(null)
        // Use the hook to handle booking conflicts
        handleBookingConflictError(error)
      })
  }

  const handleSaveAndAddNew = (values) => {
    setCurrentAction('saveAndAddNew')
    return dispatch(createCrewTimeOff(crewMemberId, values))
      .then(() => {
        toaster.success(t("crewMember.scheduledTimeOff.messages.createSuccess"))
        setEditingRecord(null)
        setCurrentAction(null)
        fetchTimeOffData()
      })
      .catch((error) => {
        setCurrentAction(null)
        // Use the hook to handle booking conflicts
        handleBookingConflictError(error)
      })
  }

  // Transform API data to component format
  const transformedData = timeOffData.map((item) => ({
    id: item.id,
    day: item.day_range,
    date: item.date_display,
    time: item.time_display,
    start_date: item.start_date,
    end_date: item.end_date,
    start_time: item.start_time,
    end_time: item.end_time,
    part_day: item.part_day,
    reason: item.reason,
    notes: item.notes,
  }))

  // Handle table change (sorting)
  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.field) {
      setSortKey(sorter.field)
      setSortValue(sorter.order === "ascend" ? "asc" : "desc")
    } else {
      setSortKey(null)
      setSortValue(null)
    }
  }

  // Show loading state
  if (loading && !timeOffData.length) {
    return (
      <Flex vertical gap={16} className={styles.container}>
        <Flex align="center" justify="space-between" className={styles.header}>
          <h2 className={styles.title}>{t("crewMember.scheduledTimeOff.table.title")}</h2>
        </Flex>
        <Card className="py-5xl">
          <Flex justify="center" align="center">
            <Spin size="large" />
          </Flex>
        </Card>
      </Flex>
    )
  }

  // Helper function to get sort order for a column
  const getSortOrder = (columnKey) => {
    if (sortKey !== columnKey) {
      return null
    }
    return sortValue === "asc" ? "ascend" : "descend"
  }

  // Define table columns
  const columns = [
    {
      title: t("crewMember.scheduledTimeOff.table.columns.day"),
      dataIndex: "day",
      key: "day",
      sorter: true,
      sortOrder: getSortOrder("day")
    },
    {
      title: t("crewMember.scheduledTimeOff.table.columns.dates"),
      dataIndex: "date",
      key: "date",
      sorter: true,
      sortOrder: getSortOrder("date")
    },
    {
      title: t("crewMember.scheduledTimeOff.table.columns.time"),
      dataIndex: "time",
      key: "time",
      sorter: true,
      sortOrder: getSortOrder("time"),
      render: (time) => {
        return (
          <Text>{time}</Text>
        )
      }
    },
    can_edit && {
      title: t("crewMember.scheduledTimeOff.table.columns.actions"),
      key: "actions",
      align: "right",
      width: 80,
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "edit",
                icon: <EditOutlined />,
                label: t("crewMember.scheduledTimeOff.table.actions.edit"),
                onClick: () => handleEdit(record)
              },
              {
                key: "delete",
                icon: <DeleteOutlined />,
                label: t("crewMember.scheduledTimeOff.table.actions.delete"),
                danger: true,
                onClick: () => handleDelete(record)
              }
            ]
          }}
          placement="bottomRight"
        >
          <Button type="text" icon={<MoreOutlined />} className={styles.moreButton} />
        </Dropdown>
      )
    }
  ].filter(Boolean)

  return (
    <>
      <Flex vertical gap={16} className={styles.container}>
        {/* Header */}
        <Flex align="center" justify="space-between" className={styles.header}>
          <h2 className={styles.title}>{t("crewMember.scheduledTimeOff.table.title")}</h2>
          {can_edit && (
            <Button type="link" onClick={handleAdd} className={styles.addButton}>
              {t("crewMember.scheduledTimeOff.buttons.add")}
            </Button>
          )}
        </Flex>

        {/* Table */}
        <WithEmptyStateWrapper
          name="time off"
          hasItems={transformedData.length > 0}
          fallback={
            <Card className="py-5xl">
              <Flex vertical justify="center" align="center" gap={12}>
                <BookingEmptyImage />
                <Text color={colors.textPrimary} weight="semibold">
                  {t("crewMember.scheduledTimeOff.emptyState.title")}
                </Text>
                <Text color={colors.textSecondaryDark}>
                  {t("crewMember.scheduledTimeOff.emptyState.subtitle")}
                </Text>
              </Flex>
            </Card>
          }
        >
          <Flex vertical className={styles.tableContainer}>
            <DataTable
              columns={columns}
              dataSource={transformedData}
              pagination={false}
              rowClickKey="id"
              onChange={handleTableChange}
            />

            {/* Pagination */}
            <CustomPagination
              metadata={metadata}
              pageSize={pageSize}
              total={metadata?.total_results || 0}
              onPageChange={setCurrentPage}
              showPageSizeSelector={false}
            />
          </Flex>
        </WithEmptyStateWrapper>
      </Flex>

      {/* Schedule Time Off Modal */}
      <ScheduleTimeOffModal
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false)
          setEditingRecord(null)
          setCurrentAction(null)
        }}
        onSave={handleSave}
        onSaveAndAddNew={handleSaveAndAddNew}
        onDeleteTimeOff={handleDelete}
        onEditTimeOff={handleEdit}
        editMode={!!editingRecord}
        initialValues={editingRecord}
        timeOffData={transformedData}
        saveLoading={currentAction === 'save' && (creating || updating)}
        saveAndAddNewLoading={currentAction === 'saveAndAddNew' && creating}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteConfirmOpen}
        title={t("crewMember.scheduledTimeOff.deleteConfirmation.title")}
        info={t("crewMember.scheduledTimeOff.deleteConfirmation.message")}
        onCancel={cancelDelete}
        onDelete={confirmDelete}
        deleteLabel={t("crewMember.scheduledTimeOff.table.actions.delete")}
        cancelLabel={t("crewMember.scheduledTimeOff.buttons.cancel")}
        loading={deleting}
      />

      {/* Booking Conflict Modal */}
      <BookingConflictModal {...bookingConflictModalProps} />
    </>
  )
}

ScheduledTimeOff.propTypes = {
  crewMemberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  can_edit: PropTypes.bool
}

export default ScheduledTimeOff
